import React from "react";
import "./SuspendedDeliveriesList.css";
import { ChevronDown } from "lucide-react";
import {
  ActionButton,
  joinClasses,
  StatusBadge,
  Tooltip,
} from "./ResultsPrimitives.jsx";
import { getFleetProductTone } from "../utils/cropColors.js";
import { isUntargetedDispatchReactivationAllowed } from "../utils/dispatchReassignmentMode.js";

const CARD_SURFACE_VARIANTS = [
  {
    articleClassName: "border-[var(--dispatch-suspended-alt-border)]",
    style: {
      background: "var(--dispatch-suspended-alt-surface, var(--results-shell-periphery))",
      boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.72)",
    },
  },
  {
    articleClassName: "border-[var(--dispatch-suspended-info-border)]",
    style: {
      background: "var(--dispatch-suspended-info-surface, linear-gradient(180deg, rgba(243, 248, 252, 0.98), rgba(255, 255, 255, 0.98)))",
      boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.78)",
    },
  },
];

const MEMBER_CARD_SURFACE = {
  articleClassName: "border-[var(--dispatch-suspended-detail-border)]",
  style: {
    background: "var(--dispatch-suspended-detail-surface, linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.99)))",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.78)",
  },
};

const SUSPENDED_ACTIVATION_HIGHLIGHT_CLASS_NAME = "relative z-10 ring-2 ring-[rgba(217,119,6,0.42)] shadow-[0_0_0_1px_rgba(217,119,6,0.14)] motion-safe:animate-[fleet-timeline-drop-highlight_0.92s_ease-in-out_5]";
const SUSPENDED_CARD_HOVER_CLASS_NAME = "suspended-card-interaction";
const SUSPENDED_BLOCKED_PLANNING_MOVEMENT_CLASS_NAME = "rounded-[4px]! border-border-danger-strong! outline outline-2 outline-offset-[-2px] outline-[var(--color-danger)] cursor-not-allowed!";
const SUSPENDED_BLOCKED_PLANNING_MOVEMENT_PULSE_CLASS_NAME = "pointer-events-none absolute inset-0 z-30 rounded-[4px] border-2 border-border-danger-strong motion-safe:animate-[pulse_420ms_ease-in-out_2] motion-reduce:animate-none";

const FAST_UNTARGETED_REACTIVATION_DISABLED_REASON = "Mode Rapide : glissez cette commande sur un camion compatible depuis la vue Flotte, ou passez en mode Complet.";

function getSuspendedCardSurface(index = 0) {
  return CARD_SURFACE_VARIANTS[Math.abs(index) % CARD_SURFACE_VARIANTS.length] || CARD_SURFACE_VARIANTS[0];
}

function isNestedSuspendedCardControl(event) {
  return typeof Element !== "undefined"
    && event?.target instanceof Element
    && Boolean(event.target.closest("button, a, input, label, textarea, select, [contenteditable='true']"));
}

function isBlockedSuspendedPlanningMovementSource(
  blockedPlanningMovementFeedback,
  item,
  sourceType,
) {
  const dispatchPeriod = item?.dispatchPeriod === "evening" ? "evening" : "day";
  if (
    blockedPlanningMovementFeedback?.sourceSurface !== "suspended"
    || blockedPlanningMovementFeedback?.sourceType !== sourceType
    || blockedPlanningMovementFeedback?.dispatchPeriod !== dispatchPeriod
  ) {
    return false;
  }

  if (sourceType === "group") {
    return Boolean(
      blockedPlanningMovementFeedback?.groupKey
      && blockedPlanningMovementFeedback.groupKey === (item?.groupKey || item?.key),
    );
  }

  return Boolean(
    blockedPlanningMovementFeedback?.deliveryId
    && blockedPlanningMovementFeedback.deliveryId === item?.deliveryId,
  );
}

function SuspendedCardPills({
  compact = false,
  item,
  productTone = null,
}) {
  if (!item?.quantityLabel && !item?.productLabel) {
    return null;
  }

  const pillClassName = "inline-flex h-5 shrink-0 items-center rounded-[4px] border px-1.5 py-0 font-semibold leading-none whitespace-nowrap";

  return (
    <div className="flex shrink-0 items-center gap-1">
      {item.quantityLabel ? (
        <span
          className={joinClasses([
            pillClassName,
            compact ? "text-[0.68rem]" : "text-[0.72rem]",
            "border-[var(--dispatch-suspended-info-border)] bg-[var(--dispatch-suspended-quantity-fill)] text-content-ink",
          ])}
          title={item.quantityLabel}
        >
          {item.quantityLabel}
        </span>
      ) : null}
      {item.productLabel ? (
        <span
          className={joinClasses([
            pillClassName,
            compact ? "text-[0.6rem]" : "text-[0.64rem]",
            productTone ? "text-white" : "border-[rgba(15,23,42,0.08)] bg-[rgba(255,255,255,0.72)] text-content-copy",
          ])}
          style={productTone ? { backgroundColor: productTone.accentColor, borderColor: productTone.accentColor } : undefined}
          title={item.productLabel}
        >
          {item.productLabel}
        </span>
      ) : null}
    </div>
  );
}

function SuspendedOrderDetails({
  buildingLabel = "",
  compact = false,
  orderLabel = "",
  siloLabel = "",
  workOrderLabel = "",
}) {
  if (!orderLabel && !workOrderLabel && !siloLabel && !buildingLabel) {
    return null;
  }

  const orderSizeClassName = compact ? "text-[0.61rem]" : "text-[0.67rem]";
  const detailSizeClassName = compact ? "text-[0.58rem]" : "text-[0.62rem]";

  return (
    <div className="flex min-w-0 items-center justify-between gap-2">
      {orderLabel || workOrderLabel ? (
        <p
          className={joinClasses(["m-0 flex min-w-0 flex-1 items-center gap-1.5 truncate leading-tight", orderSizeClassName])}
          title={[orderLabel ? `Commande ${orderLabel}` : "", workOrderLabel ? `Bon de travail ${workOrderLabel}` : ""].filter(Boolean).join(" · ")}
        >
          {orderLabel ? (
            <span className="truncate font-semibold text-content-copy">{orderLabel}</span>
          ) : null}
          {workOrderLabel ? (
            <span className="truncate font-semibold text-content-copy">{workOrderLabel}</span>
          ) : null}
        </p>
      ) : (
        <span className="min-w-0 flex-1" />
      )}
      {siloLabel || buildingLabel ? (
        <div className={joinClasses([
          "flex shrink-0 items-center gap-2 text-right leading-tight text-(--muted-strong)",
          detailSizeClassName,
        ])}>
          {siloLabel ? (
            <span className="whitespace-nowrap" title={`Silo ${siloLabel}`}>
              <span className="font-semibold text-content-copy">Silo</span> {siloLabel}
            </span>
          ) : null}
          {buildingLabel ? (
            <span className="whitespace-nowrap" title={`Bâtiment ${buildingLabel}`}>
              <span className="font-semibold text-content-copy">Bât.</span> {buildingLabel}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SuspendedDeliveryCard({
  blockedPlanningMovementFeedback = null,
  compact = false,
  draggableCards = false,
  index = 0,
  item,
  memberCard = false,
  onDeliveryDragEnd = null,
  onDeliveryDragStart = null,
  onDeliveryPointerDown = null,
  onBlockedPlanningMovementAttempt = null,
  onPreviewDelivery = null,
  planningMovementBlocked = false,
  consumeReassignmentClickSuppression = null,
  onReactivateDelivery = null,
  previewedDeliveryId = "",
  untargetedReactivationDisabledReason = "",
}) {
  const canAttemptPlanningMovement = draggableCards && !item.isPending && Boolean(item?.deliveryId);
  const isDraggable = canAttemptPlanningMovement && !planningMovementBlocked;
  const showCompactActions = !compact;
  const canPreview = typeof onPreviewDelivery === "function" && !item.isPending;
  const isPreviewActive = previewedDeliveryId && previewedDeliveryId === item.deliveryId;
  const isUntargetedReactivationDisabled = Boolean(untargetedReactivationDisabledReason);
  const shouldRenderStructuredLayout = memberCard && Boolean(item.loadingName || item.deliveryName || item.orderLabel);
  const surface = memberCard ? MEMBER_CARD_SURFACE : getSuspendedCardSurface(index);
  const productTone = item.productLabel ? getFleetProductTone(item.productLabel) : null;
  const primaryClientName = memberCard
    ? (item.loadingName || "Chargement à confirmer")
    : (item.deliveryName || item.title || "Client à confirmer");
  const titleSizeClassName = compact ? "text-[0.75rem]" : "text-[0.82rem]";
  const addressSizeClassName = compact ? "text-[0.64rem]" : "text-[0.7rem]";
  const isBlockedPlanningMovementSource = isBlockedSuspendedPlanningMovementSource(
    blockedPlanningMovementFeedback,
    item,
    "delivery",
  );
  const attemptBlockedPlanningMovement = (event) => {
    if (
      !planningMovementBlocked
      || !canAttemptPlanningMovement
      || (event?.type === "pointerdown" && (event.button !== 0 || !event.isPrimary))
      || isNestedSuspendedCardControl(event)
    ) {
      return false;
    }

    return onBlockedPlanningMovementAttempt?.(event, {
      deliveryId: item.deliveryId,
      dispatchPeriod: item?.dispatchPeriod === "evening" ? "evening" : "day",
      sourceSurface: "suspended",
      sourceType: "delivery",
    }) === true;
  };

  return (
    <article
      className={joinClasses([
        "relative grid rounded-[4px] border transition duration-150",
        SUSPENDED_CARD_HOVER_CLASS_NAME,
        surface.articleClassName,
        isDraggable ? "cursor-grab select-none active:cursor-grabbing" : canPreview ? "cursor-pointer" : "",
        compact ? "gap-0.75 p-[8px_10px]" : "gap-1 p-[11px_13px]",
        item?.isActivationHighlighted ? SUSPENDED_ACTIVATION_HIGHLIGHT_CLASS_NAME : "",
        isPreviewActive ? "ring-2 ring-[rgba(217,119,6,0.24)] ring-offset-0" : "",
        isBlockedPlanningMovementSource ? SUSPENDED_BLOCKED_PLANNING_MOVEMENT_CLASS_NAME : "",
      ])}
      data-suspended-activation-highlighted={item?.isActivationHighlighted ? "true" : undefined}
      data-suspended-delivery-card="true"
      data-dispatch-period={item?.dispatchPeriod === "evening" ? "evening" : "day"}
      data-planning-movement-feedback={isBlockedPlanningMovementSource ? "blocked" : undefined}
      draggable={isDraggable}
      onClick={(event) => {
        if (planningMovementBlocked && canAttemptPlanningMovement) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (!canPreview || consumeReassignmentClickSuppression?.()) {
          return;
        }

        onPreviewDelivery?.(item);
      }}
      onDragEnd={(event) => {
        onDeliveryDragEnd?.(event);
      }}
      onDragStart={(event) => {
        if (attemptBlockedPlanningMovement(event)) {
          event.preventDefault();
          return;
        }
        onDeliveryDragStart?.(event, item);
      }}
      onPointerDown={(event) => {
        if (attemptBlockedPlanningMovement(event)) {
          return;
        }
        onDeliveryPointerDown?.(event, item);
      }}
      onKeyDown={(event) => {
        if (!canPreview || (event.key !== "Enter" && event.key !== " ")) {
          return;
        }

        event.preventDefault();
        if (attemptBlockedPlanningMovement(event)) {
          return;
        }
        onPreviewDelivery?.(item);
      }}
      aria-disabled={planningMovementBlocked && canAttemptPlanningMovement ? "true" : undefined}
      role={canPreview ? "button" : undefined}
      tabIndex={canPreview ? 0 : undefined}
      style={surface.style}
    >
      {isBlockedPlanningMovementSource ? (
        <span
          key={blockedPlanningMovementFeedback.attemptNonce}
          aria-hidden="true"
          className={SUSPENDED_BLOCKED_PLANNING_MOVEMENT_PULSE_CLASS_NAME}
        />
      ) : null}
      {shouldRenderStructuredLayout ? (
        <div className={joinClasses(["min-w-0", showCompactActions ? "flex items-start justify-between gap-2.5" : "grid gap-0.75"])}>
          <div className="grid min-w-0 gap-0.75">
            <div className="grid gap-px min-w-0">
              <div className="flex min-w-0 items-start gap-2">
                <strong className={joinClasses(["block min-w-0 flex-1 truncate leading-[1.32] text-content-ink", titleSizeClassName])}>
                  {primaryClientName}
                </strong>
                <SuspendedCardPills compact={compact} item={item} productTone={productTone} />
              </div>
              <SuspendedOrderDetails
                buildingLabel={item.buildingLabel}
                compact={compact}
                orderLabel={item.orderLabel}
                siloLabel={item.siloLabel}
                workOrderLabel={item.workOrderLabel}
              />
              {(memberCard ? item.loadingAddress : item.deliveryAddress) ? (
                <p
                  className={joinClasses(["m-0 truncate leading-[1.38] text-(--muted-strong)", addressSizeClassName])}
                  title={memberCard ? item.loadingAddress : item.deliveryAddress}
                >
                  {memberCard ? item.loadingAddress : item.deliveryAddress}
                </p>
              ) : null}
            </div>

            {memberCard ? (
              <div className="grid gap-px min-w-0">
                <strong className={joinClasses(["block truncate leading-[1.32] text-content-ink", titleSizeClassName])}>
                  {item.deliveryName || item.title}
                </strong>
                {item.deliveryAddress ? (
                  <p className={joinClasses(["m-0 truncate leading-[1.38] text-(--muted-strong)", addressSizeClassName])} title={item.deliveryAddress}>
                    {item.deliveryAddress}
                  </p>
                ) : null}
              </div>
            ) : null}

          </div>

          {showCompactActions ? (
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <StatusBadge tone="warning" className="px-2 py-1 text-[0.62rem]">
                {item.badgeLabel}
              </StatusBadge>
              <Tooltip
                title="Choisissez un camion"
                content={untargetedReactivationDisabledReason}
                disabled={!isUntargetedReactivationDisabled}
              >
                <ActionButton
                  tone="ghost"
                  size="sm"
                  disabled={item.isPending || isUntargetedReactivationDisabled}
                  className="justify-start px-2.25 py-0.9 text-[0.68rem]"
                  data-suspended-untargeted-reactivation="true"
                  onClick={(event) => {
                    event.stopPropagation();
                    onReactivateDelivery?.(item.deliveryId, {
                      dispatchPeriod: item?.dispatchPeriod === "evening" ? "evening" : "day",
                      expectedRuntimeRevision: item?.runtimeRevision || "",
                    });
                  }}
                >
                  Réintégrer
                </ActionButton>
              </Tooltip>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className={joinClasses([
            "min-w-0 gap-1",
            compact ? "grid" : "flex flex-wrap items-start justify-between",
          ])}>
            <div className="min-w-0">
              <strong className={joinClasses([
                "block truncate leading-[1.35] text-content-ink",
                compact ? "text-[0.76rem]" : "text-[0.92rem]",
              ])}>
                {item.title}
              </strong>
              <p className={joinClasses([
                "m-0 leading-[1.45] text-(--muted-strong)",
                compact ? "text-[0.68rem]" : "text-[0.82rem]",
              ])}>
                {item.subtitle}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <SuspendedCardPills compact={compact} item={item} productTone={productTone} />
              {showCompactActions ? (
                <StatusBadge tone="warning" className={compact ? "justify-self-start px-2 py-1 text-[0.62rem]" : ""}>
                  {item.badgeLabel}
                </StatusBadge>
              ) : null}
            </div>
          </div>
        </>
      )}

      {!shouldRenderStructuredLayout && showCompactActions ? (
        <Tooltip
          title="Choisissez un camion"
          content={untargetedReactivationDisabledReason}
          disabled={!isUntargetedReactivationDisabled}
        >
          <ActionButton
            tone="ghost"
            size="sm"
            disabled={item.isPending || isUntargetedReactivationDisabled}
            className={compact ? "justify-start px-2.5 py-1 text-[0.7rem]" : "justify-start"}
            data-suspended-untargeted-reactivation="true"
            onClick={(event) => {
              event.stopPropagation();
              onReactivateDelivery?.(item.deliveryId, {
                dispatchPeriod: item?.dispatchPeriod === "evening" ? "evening" : "day",
                expectedRuntimeRevision: item?.runtimeRevision || "",
              });
            }}
          >
            Réintégrer
          </ActionButton>
        </Tooltip>
      ) : null}
    </article>
  );
}

function SuspendedDeliveryGroupCard({
  blockedPlanningMovementFeedback = null,
  compact = false,
  draggableCards = false,
  expanded = false,
  index = 0,
  item,
  onDeliveryDragEnd = null,
  onDeliveryDragStart = null,
  onDeliveryPointerDown = null,
  onBlockedPlanningMovementAttempt = null,
  onPreviewDelivery = null,
  planningMovementBlocked = false,
  consumeReassignmentClickSuppression = null,
  onReactivateDelivery = null,
  renderGroupTrailingAction = null,
  onToggleExpanded = null,
  previewedDeliveryId = "",
  untargetedReactivationDisabledReason = "",
}) {
  const memberItems = Array.isArray(item?.memberItems) ? item.memberItems.filter(Boolean) : [];
  const deliveryIds = (Array.isArray(item?.deliveryIds) ? item.deliveryIds : [item?.deliveryId])
    .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
    .map((deliveryId) => deliveryId.trim());
  const canAttemptPlanningMovement = draggableCards && !item.isPending && deliveryIds.length > 0;
  const isDraggable = canAttemptPlanningMovement && !planningMovementBlocked;
  const canPreview = typeof onPreviewDelivery === "function" && !item.isPending;
  const isPreviewActive = memberItems.some((member) => previewedDeliveryId && previewedDeliveryId === member.deliveryId);
  const surface = getSuspendedCardSurface(index);
  const productTone = item.productLabel ? getFleetProductTone(item.productLabel) : null;
  const groupPanelId = `suspended-group-${String(item.groupKey || item.key || index).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const trailingAction = typeof renderGroupTrailingAction === "function"
    ? renderGroupTrailingAction(item)
    : null;
  const isBlockedPlanningMovementSource = isBlockedSuspendedPlanningMovementSource(
    blockedPlanningMovementFeedback,
    item,
    "group",
  );
  const attemptBlockedPlanningMovement = (event) => {
    if (
      !planningMovementBlocked
      || !canAttemptPlanningMovement
      || (event?.type === "pointerdown" && (event.button !== 0 || !event.isPrimary))
      || isNestedSuspendedCardControl(event)
    ) {
      return false;
    }

    return onBlockedPlanningMovementAttempt?.(event, {
      deliveryId: deliveryIds[0] || "",
      deliveryIds,
      dispatchPeriod: item?.dispatchPeriod === "evening" ? "evening" : "day",
      groupKey: item?.groupKey || item?.key || "",
      sourceSurface: "suspended",
      sourceType: "group",
    }) === true;
  };

  return (
    <article
      className={joinClasses([
        "relative grid overflow-hidden rounded-[4px] border transition duration-150",
        surface.articleClassName,
        isPreviewActive ? "ring-2 ring-[rgba(217,119,6,0.24)] ring-offset-0" : "",
        isBlockedPlanningMovementSource ? SUSPENDED_BLOCKED_PLANNING_MOVEMENT_CLASS_NAME : "",
      ])}
      data-suspended-activation-highlighted={item?.isActivationHighlighted ? "true" : undefined}
      data-suspended-delivery-group-card="true"
      data-dispatch-period={item?.dispatchPeriod === "evening" ? "evening" : "day"}
      data-planning-movement-feedback={isBlockedPlanningMovementSource ? "blocked" : undefined}
      style={surface.style}
    >
      {isBlockedPlanningMovementSource ? (
        <span
          key={blockedPlanningMovementFeedback.attemptNonce}
          aria-hidden="true"
          className={SUSPENDED_BLOCKED_PLANNING_MOVEMENT_PULSE_CLASS_NAME}
        />
      ) : null}
      <div
        className={joinClasses([
          "flex min-w-0 items-center justify-between gap-2",
          SUSPENDED_CARD_HOVER_CLASS_NAME,
          isBlockedPlanningMovementSource
            ? "cursor-not-allowed!"
            : isDraggable
              ? "cursor-grab select-none active:cursor-grabbing"
              : canPreview
                ? "cursor-pointer"
                : "",
          compact ? "p-[8px_10px]" : "p-[11px_13px]",
          item?.isActivationHighlighted ? SUSPENDED_ACTIVATION_HIGHLIGHT_CLASS_NAME : "",
        ])}
        data-suspended-activation-highlighted={item?.isActivationHighlighted ? "true" : undefined}
        draggable={isDraggable}
        onClick={(event) => {
          if (planningMovementBlocked && canAttemptPlanningMovement) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          if (!canPreview || consumeReassignmentClickSuppression?.()) {
            return;
          }
          onPreviewDelivery?.(item);
        }}
        onDragEnd={(event) => {
          onDeliveryDragEnd?.(event);
        }}
        onDragStart={(event) => {
          if (attemptBlockedPlanningMovement(event)) {
            event.preventDefault();
            return;
          }
          onDeliveryDragStart?.(event, item);
        }}
        onPointerDown={(event) => {
          if (attemptBlockedPlanningMovement(event)) {
            return;
          }
          onDeliveryPointerDown?.(event, item);
        }}
        onKeyDown={(event) => {
          if (!canPreview || (event.key !== "Enter" && event.key !== " ")) {
            return;
          }

          event.preventDefault();
          if (attemptBlockedPlanningMovement(event)) {
            return;
          }
          onPreviewDelivery?.(item);
        }}
        aria-disabled={planningMovementBlocked && canAttemptPlanningMovement ? "true" : undefined}
        role={canPreview ? "button" : undefined}
        tabIndex={canPreview ? 0 : undefined}
      >
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35"
            aria-expanded={expanded}
            aria-controls={groupPanelId}
            title={expanded ? "Replier le groupe" : "Déplier le groupe"}
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpanded?.(item.groupKey || item.key);
            }}
          >
            <ChevronDown
              aria-hidden="true"
              className={joinClasses([
                "h-3.5 w-3.5 transition-transform",
                expanded ? "rotate-0" : "-rotate-90",
              ])}
              strokeWidth={2.2}
            />
          </button>
          <div className="grid min-w-0 gap-0.5">
            <strong className={joinClasses([
              "block min-w-0 truncate leading-[1.32] text-content-ink",
              compact ? "text-[0.75rem]" : "text-[0.82rem]",
            ])}>
              {item.title}
            </strong>
            <p className={joinClasses([
              "m-0 truncate font-medium leading-[1.35] text-(--muted-strong)",
              compact ? "text-[0.66rem]" : "text-[0.72rem]",
            ])}>
              {item.subtitle}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <SuspendedCardPills compact={compact} item={item} productTone={productTone} />
          {trailingAction}
        </div>
      </div>

      {expanded ? (
        <div id={groupPanelId} className="grid gap-0.75 border-t border-[rgba(148,163,184,0.12)] bg-[rgba(255,255,255,0.88)] p-1.5">
          {memberItems.map((memberItem) => (
            <SuspendedDeliveryCard
              key={memberItem.key}
              blockedPlanningMovementFeedback={blockedPlanningMovementFeedback}
              compact={compact}
              draggableCards={draggableCards}
              item={memberItem}
              memberCard
              onDeliveryDragEnd={onDeliveryDragEnd}
              onDeliveryDragStart={onDeliveryDragStart}
              onDeliveryPointerDown={onDeliveryPointerDown}
              onBlockedPlanningMovementAttempt={onBlockedPlanningMovementAttempt}
              onPreviewDelivery={onPreviewDelivery}
              planningMovementBlocked={planningMovementBlocked}
              consumeReassignmentClickSuppression={consumeReassignmentClickSuppression}
              onReactivateDelivery={onReactivateDelivery}
              previewedDeliveryId={previewedDeliveryId}
              untargetedReactivationDisabledReason={untargetedReactivationDisabledReason}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

// Tonnage suspendu total et par produit (les groupes comptent chacune de leurs commandes).
export function resolveSuspendedTonnageSummary(items = []) {
  const tonsByProduct = new Map();
  let totalTons = 0;
  const deliveries = (Array.isArray(items) ? items : []).flatMap((item) => (
    item?.type === "group" && Array.isArray(item.memberItems) ? item.memberItems : [item]
  ));
  for (const delivery of deliveries) {
    const tons = Number(delivery?.quantityTons);
    if (!Number.isFinite(tons) || tons <= 0) {
      continue;
    }
    totalTons += tons;
    const productLabel = typeof delivery?.productLabel === "string" ? delivery.productLabel.trim() : "";
    const key = productLabel.toLocaleLowerCase("fr-CA");
    const entry = tonsByProduct.get(key) || { label: productLabel, tons: 0 };
    entry.tons += tons;
    tonsByProduct.set(key, entry);
  }
  return {
    totalTons,
    products: [...tonsByProduct.values()].filter((entry) => entry.label).sort((left, right) => right.tons - left.tons),
  };
}

const formatSummaryTons = (tons) => `${tons.toLocaleString("fr-CA", { maximumFractionDigits: 1, minimumFractionDigits: 1 })} t`;

export function SuspendedTonnageSummary({ items = [] }) {
  const { products, totalTons } = resolveSuspendedTonnageSummary(items);
  if (!totalTons) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1 rounded-[4px] border border-[rgba(148,163,184,0.3)] bg-white px-2 py-1"
      data-suspended-tonnage-summary="true"
    >
      <span className="text-[0.72rem] font-extrabold tabular-nums text-content-ink">{formatSummaryTons(totalTons)}</span>
      {products.map(({ label, tons }) => {
        const { accentColor } = getFleetProductTone(label);
        return (
          <span
            key={label}
            className="inline-flex h-4.5 items-center gap-1 rounded-[4px] px-1.5 text-[0.6rem] font-bold leading-none text-white"
            style={{ backgroundColor: accentColor }}
          >
            {label}
            <span className="font-semibold tabular-nums opacity-85">{formatSummaryTons(tons)}</span>
          </span>
        );
      })}
    </div>
  );
}

export default function SuspendedDeliveriesList({
  blockedPlanningMovementFeedback = null,
  compact = false,
  draggableCards = false,
  items = [],
  onDeliveryDragEnd = null,
  onDeliveryDragStart = null,
  onDeliveryPointerDown = null,
  onBlockedPlanningMovementAttempt = null,
  onPreviewDelivery = null,
  planningMovementBlocked = false,
  consumeReassignmentClickSuppression = null,
  onReactivateDelivery = null,
  reassignmentRebuildMode = "complete",
  renderGroupTrailingAction = null,
  previewedDeliveryId = "",
}) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [];
  const untargetedReactivationDisabledReason = isUntargetedDispatchReactivationAllowed(reassignmentRebuildMode)
    ? ""
    : FAST_UNTARGETED_REACTIVATION_DISABLED_REASON;
  const [expandedGroupKeys, setExpandedGroupKeys] = React.useState(() => new Set());

  const toggleExpandedGroup = React.useCallback((groupKey = "") => {
    setExpandedGroupKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      if (nextKeys.has(groupKey)) {
        nextKeys.delete(groupKey);
      } else {
        nextKeys.add(groupKey);
      }
      return nextKeys;
    });
  }, []);

  return (
    <>
      {resolvedItems.map((item, index) => {
        if (item?.type === "group") {
          const groupKey = item.groupKey || item.key;
          return (
            <SuspendedDeliveryGroupCard
              key={item.key}
              blockedPlanningMovementFeedback={blockedPlanningMovementFeedback}
              compact={compact}
              draggableCards={draggableCards}
              expanded={expandedGroupKeys.has(groupKey)}
              index={index}
              item={item}
              onDeliveryDragEnd={onDeliveryDragEnd}
              onDeliveryDragStart={onDeliveryDragStart}
              onDeliveryPointerDown={onDeliveryPointerDown}
              onBlockedPlanningMovementAttempt={onBlockedPlanningMovementAttempt}
              onPreviewDelivery={onPreviewDelivery}
              planningMovementBlocked={planningMovementBlocked}
              consumeReassignmentClickSuppression={consumeReassignmentClickSuppression}
              onReactivateDelivery={onReactivateDelivery}
              renderGroupTrailingAction={renderGroupTrailingAction}
              onToggleExpanded={toggleExpandedGroup}
              previewedDeliveryId={previewedDeliveryId}
              untargetedReactivationDisabledReason={untargetedReactivationDisabledReason}
            />
          );
        }

        return (
          <SuspendedDeliveryCard
            key={item.key}
            blockedPlanningMovementFeedback={blockedPlanningMovementFeedback}
            compact={compact}
            draggableCards={draggableCards}
            index={index}
            item={item}
            onDeliveryDragEnd={onDeliveryDragEnd}
            onDeliveryDragStart={onDeliveryDragStart}
            onDeliveryPointerDown={onDeliveryPointerDown}
            onBlockedPlanningMovementAttempt={onBlockedPlanningMovementAttempt}
            onPreviewDelivery={onPreviewDelivery}
            planningMovementBlocked={planningMovementBlocked}
            consumeReassignmentClickSuppression={consumeReassignmentClickSuppression}
            onReactivateDelivery={onReactivateDelivery}
            previewedDeliveryId={previewedDeliveryId}
            untargetedReactivationDisabledReason={untargetedReactivationDisabledReason}
          />
        );
      })}
    </>
  );
}
